// frontend/src/services/api.ts
import axios, { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import { MFALoginResponse } from '../types/mfa';

// ✅ FIXED: Use environment variable with fallback to localhost for development
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8000/api';

// Log the API URL being used (helps with debugging)
console.log(`🔧 [API] Configured to use: ${API_BASE_URL}`);

// Custom error type for MFA
class MFARequiredError extends Error {
  response?: {
    data: MFALoginResponse;
    status: number;
    statusText: string;
  };

  constructor(message: string, responseData?: MFALoginResponse, status?: number, statusText?: string) {
    super(message);
    this.name = 'MFARequiredError';
    if (responseData && status !== undefined) {
      this.response = {
        data: responseData,
        status,
        statusText: statusText || 'MFA Required'
      };
    }
  }
}

// Define the structure for error response data
interface ErrorResponseData {
  requires_mfa?: boolean;
  detail?: any;
  [key: string]: any; // Allow other properties
}

// Define the structure for axios error response
interface AxiosErrorResponse<T = ErrorResponseData> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
}

// Create axios instance - INCREASED TIMEOUT to 2 minutes for cold starts
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // ✅ CHANGED: 120000ms (2 minutes) from 60000ms to handle Render cold starts
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to safely get Authorization header as string
const getAuthorizationHeader = (): string | null => {
  const authHeader = api.defaults.headers.common['Authorization'];
  
  if (!authHeader) {
    return null;
  }
  
  // Handle different possible types
  if (typeof authHeader === 'string') {
    return authHeader;
  }
  
  if (Array.isArray(authHeader)) {
    return authHeader[0] as string;
  }
  
  // For AxiosHeaders or other types, convert to string
  return String(authHeader);
};

// Helper function to safely get substring of Authorization header
const getAuthHeaderSubstring = (start: number, end: number): string => {
  const authHeader = getAuthorizationHeader();
  if (!authHeader || typeof authHeader !== 'string') {
    return 'NOT SET';
  }
  return authHeader.substring(start, end) + '...';
};

// ✅ Request interceptor for auth token - WITH DEBUG LOGGING
api.interceptors.request.use(
  (config) => {
    // Check token in localStorage AND in axios defaults for debugging
    const tokenFromStorage = localStorage.getItem('token');
    const tokenFromAxios = getAuthorizationHeader();
    
    console.log(`🔍 [API] Request to ${config.url}:`);
    console.log(`  - Base URL: ${API_BASE_URL}`);
    console.log(`  - Full URL: ${config.baseURL}${config.url}`);
    console.log(`  - Token in localStorage: ${tokenFromStorage ? `Present (${tokenFromStorage.length} chars)` : 'MISSING'}`);
    console.log(`  - Token in axios defaults: ${tokenFromAxios ? 'Set' : 'NOT SET'}`);
    
    // Always use token from storage
    if (tokenFromStorage) {
      config.headers.Authorization = `Bearer ${tokenFromStorage}`;
      console.log(`✅ [API] Authorization header set for ${config.url}`);
      
      // Also ensure axios defaults are synchronized
      if (!tokenFromAxios) {
        api.defaults.headers.common['Authorization'] = `Bearer ${tokenFromStorage}`;
        console.log(`🔄 [API] Fixed: Updated axios defaults with token from storage`);
      }
    } else {
      console.log(`❌ [API] No token found for ${config.url}`);
    }
    return config;
  },
  (error) => {
    console.error(`❌ [API] Request interceptor error:`, error);
    return Promise.reject(error);
  }
);

// ✅ Response interceptor with MFA and 401 handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ [API] Response from ${response.config.url}: ${response.status}`);
    return response;
  },
  (error: AxiosError<ErrorResponseData>) => {
    const url = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    console.log(`❌ [API] ${method} ${url}: ${error.response?.status || 'No status'}`);
    
    // Check if error response indicates MFA is required
    if (error.response?.data?.requires_mfa) {
      console.log(`🔐 [API] MFA required detected for ${url}`);
      // Create a custom error that indicates MFA is required
      throw new MFARequiredError(
        'MFA_REQUIRED',
        error.response.data as MFALoginResponse,
        error.response.status,
        error.response.statusText
      );
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      console.log(`⚠️ [API] 401 Unauthorized for ${url}`);
      console.log(`🔍 [API] Debug information for 401:`);
      console.log(`  - URL: ${url}`);
      console.log(`  - Method: ${method}`);
      console.log(`  - Base URL: ${API_BASE_URL}`);
      
      // Safely get token from localStorage
      const storedToken = localStorage.getItem('token');
      console.log(`  - Token in localStorage:`, storedToken ? `${storedToken.substring(0, 30)}...` : 'Missing');
      
      // Safely get axios Authorization header
      const axiosHeader = getAuthorizationHeader();
      console.log(`  - Axios defaults Authorization:`, axiosHeader ? `${axiosHeader.substring(0, 50)}...` : 'NOT SET');
      
      console.log(`  - Request headers sent:`, JSON.stringify(error.config?.headers, null, 2));
      console.log(`  - Error detail:`, error.response?.data);
      
      // Check if there's a mismatch between localStorage and axios
      if (storedToken && !axiosHeader) {
        console.log(`🔄 [API] FIX ATTEMPT: Token exists in localStorage but not in axios defaults!`);
        console.log(`🔄 [API] Attempting to fix by setting axios defaults...`);
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        console.log(`✅ [API] Axios defaults updated. Retry the request.`);
      }
      
      if (!storedToken && axiosHeader) {
        console.log(`🔄 [API] FIX ATTEMPT: Token in axios defaults but not in localStorage!`);
        console.log(`🔄 [API] Clearing invalid axios header...`);
        delete api.defaults.headers.common['Authorization'];
      }
    }

    // Normal error normalization
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        error.response.data.detail = detail
          .map((err: any) => err.msg || JSON.stringify(err))
          .join(', ');
      } else if (typeof detail === 'object') {
        error.response.data.detail = JSON.stringify(detail);
      }
    }
    
    console.error(`[API] Error details:`, error.response?.data);
    return Promise.reject(error);
  }
);

// ---------------- Types ----------------
export interface User {
  message: string;
  id: string;
  email: string;
  created_at: string;
  mfa_enabled?: boolean;
  mfa_email?: string;
}

export interface TrackedPage {
  id: string;
  user_id: string;
  url: string;
  display_name: string | null;
  check_interval_minutes: number;
  is_active: boolean;
  created_at: string;
  last_checked: string | null;
  last_change_detected: string | null;
  current_version_id: string | null;
  version_count?: number;
}

export interface PageVersion {
  id: string;
  page_id: string;
  timestamp: string;
  text_content: string;
  change_significance_score?: number;
  has_ai_summary?: boolean;
  metadata: {
    url: string;
    content_length: number;
    word_count: number;
    fetched_at: string;
    store_reason?: string;
  };
}

export interface VersionDetail extends PageVersion {
  html_content?: string;
  change_metrics?: any;
  ai_summary?: any;
}

export interface ChangeLog {
  id: string;
  page_id: string;
  user_id: string;
  type: string;
  timestamp: string;
  description: string | null;
  semantic_similarity_score: number | null;
  change_significance_score?: number;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  email?: string;
  message?: string;
}

export interface CrawlResponse {
  status: string;
  url: string;
  content_length: number;
  content_preview: string | null;
  full_content: string;
}

export interface CrawlPageResponse {
  status: string;
  page_id: string;
  url: string;
  version_id: string;
  change_detected: boolean;
  ai_summary_generated?: boolean;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  scheduler_running: boolean;
  email_enabled: boolean;
  ai_enabled?: boolean;
  version: string;
}

export interface DeleteResponse {
  status: string;
  message: string;
}

// ✅ ADDED: Page Update Types
export interface PageUpdateData {
  display_name?: string;
  check_interval_hours?: number;
}

export interface UpdatedPageResponse {
  id: string;
  url: string;
  display_name: string;
  check_interval_hours: number;
  status: string;
  last_checked: string | null;
  created_at: string;
  last_change_detected: string | null;
  current_version_id: string | null;
}

// Forgot Password Types
export interface ForgotPasswordResponse {
  message: string;
  status: string;
}

export interface ResetPasswordResponse {
  message: string;
  status: string;
}

// MFA Types
export interface MFAVerifyResponse {
  access_token: string;
  token_type: string;
  email: string;
  message: string;
  mfa_session_token?: string; // ✅ NEW: For "Remember Me" feature
  expires_in?: number; // ✅ NEW: Expiry in seconds (86400 for 24 hours)
}

export interface MFASendCodeResponse {
  message: string;
}

export interface MFAStatusResponse {
  mfa_enabled: boolean;
  mfa_email?: string;
  mfa_setup_completed: boolean;
}

// ✅ ADDED: MFA Session Check Types
export interface MFASessionCheckResponse {
  mfa_required: boolean;
  mfa_valid: boolean;
  session_exists?: boolean;
  expires_at?: string;
  time_remaining_hours?: number;
}

// ✅ ADDED: Validate Token Types
export interface ValidateTokenRequest {
  token: string;
}

export interface ValidateTokenResponse {
  valid: boolean;
  email?: string;
  exp?: number;
}

// ✅ NEW: AI Summary Types
export interface AISummary {
  summary: string;
  key_changes: string[];
  change_type: 'major' | 'minor' | 'cosmetic';
  technical_impact: string;
  sentiment: 'positive' | 'negative' | 'neutral';
  recommendation: string;
  tokens_used?: number;
  generated_at?: string;
  model_used?: string;
  error?: string;
  is_fallback?: boolean;
  disabled?: boolean;
}

export interface AISummaryResponse {
  success: boolean;
  data: {
    has_summary: boolean;
    summary?: AISummary;
    generated_at?: string;
    model_used?: string;
    message?: string;
  };
}

export interface AIStatusResponse {
  enabled: boolean;
  model: string;
  summaries_enabled: boolean;
  api_key_configured: boolean;
}

export interface RegenerateSummaryResponse {
  success: boolean;
  data: {
    summary: AISummary;
    message: string;
  };
}

// ---------------- Auth API ----------------
export const authAPI = {
  register: (userData: { email: string; password: string }) =>
    api.post<User>('/auth/register', userData),

  // ✅ FIXED: Changed to 'email' field and sends as JSON
  login: (credentials: { email: string; password: string }) => {
    console.log('[Auth] Sending login request for:', credentials.email);
    console.log(`[Auth] Using API URL: ${API_BASE_URL}/auth/login`);
    return api.post<LoginResponse | MFALoginResponse>('/auth/login', {
      email: credentials.email,
      password: credentials.password
    });
  },

  // ✅ ADDED: Validate token endpoint
  validateToken: (token: string) => {
    console.log('[Auth] Validating token');
    return api.post<ValidateTokenResponse>('/auth/validate-token', { 
      token 
    });
  },

  // ✅ ADDED: Logout endpoint
  logout: async () => {
    console.log('[Auth] Logging out...');
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('[Auth] No token found, already logged out');
      return { success: true };
    }
    
    try {
      const response = await api.post('/auth/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[Auth] Logout successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('[Auth] Logout API error (non-critical):', error);
      // Don't throw - we still want to clear local state
      return { success: false };
    }
  },

  // Forgot Password endpoints
  forgotPassword: (email: string) =>
    api.post<ForgotPasswordResponse>('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post<ResetPasswordResponse>('/auth/reset-password', { 
      token, 
      new_password: newPassword 
    }),

  // ✅ UPDATED MFA Endpoints with "Remember Me" support
  verifyMFA: (email: string, mfaCode: string, rememberForDay: boolean = false) => {
    console.log('[Auth] Verifying MFA for:', email);
    console.log('[Auth] Remember for 24 hours:', rememberForDay);
    console.log('[Auth] Current axios Authorization before MFA:', getAuthorizationHeader());
    return api.post<MFAVerifyResponse>('/auth/verify-mfa', { 
      email, 
      mfa_code: mfaCode,
      remember_for_day: rememberForDay  // ✅ NEW: Send remember_for_day parameter
    });
  },

  sendMFACode: (email: string) =>
    api.post<MFASendCodeResponse>('/auth/send-mfa-code', { email }),

  getMFAStatus: (email: string) =>
    api.get<MFAStatusResponse>('/auth/mfa-status', { params: { email } }),

  setupMFA: (email: string, mfaEmail?: string) =>
    api.post<{ message: string }>('/auth/setup-mfa', { 
      email, 
      mfa_email: mfaEmail,
      enable_mfa: true 
    }),

  disableMFA: (email: string) =>
    api.post<{ message: string }>('/auth/disable-mfa', { email }),

  // ✅ NEW: Check MFA session endpoint for "Remember Me" feature
  checkMFASession: (email: string, mfaSessionToken?: string) =>
    api.post<MFASessionCheckResponse>('/auth/check-mfa-session', { 
      email, 
      mfa_session_token: mfaSessionToken 
    }),

  // ✅ New: Test endpoint to verify token is working
  testAuth: () => api.get<{ message: string; user: string }>('/auth/test', {
    validateStatus: (status) => status < 500 // Don't throw on 401/403
  }),
};

// ---------------- Pages API ----------------
export const pagesAPI = {
  getAll: () => {
    console.log('[Pages] Fetching all pages');
    console.log('[Pages] Current axios Authorization:', getAuthorizationHeader());
    return api.get<TrackedPage[]>('/pages');
  },
  
  getOne: (id: string) => api.get<TrackedPage>(`/pages/${id}`),
  
  create: (pageData: { 
    url: string; 
    display_name?: string; 
    check_interval_minutes?: number 
  }) => api.post<TrackedPage>('/pages', pageData),
  
  // ✅ ADDED: Update page endpoint - Fixed return type
  update: (id: string, pageData: PageUpdateData) => {
    console.log('[Pages] Updating page:', id, pageData);
    return api.put<UpdatedPageResponse>(`/pages/${id}`, pageData);
  },
  
  delete: (id: string) => api.delete<DeleteResponse>(`/pages/${id}`),
  
  getVersions: (pageId: string, limit: number = 10, includeSummary: boolean = false) => 
    api.get<{ success: boolean; data: PageVersion[] }>(
      `/pages/${pageId}/versions?limit=${limit}&include_summary=${includeSummary}`
    ),
  
  getVersion: (pageId: string, versionId: string, includeSummary: boolean = true) =>
    api.get<{ success: boolean; data: VersionDetail }>(
      `/pages/${pageId}/versions/${versionId}?include_summary=${includeSummary}`
    ),
  
  getByUrl: (url: string) => api.get<TrackedPage>(`/pages/by-url?url=${encodeURIComponent(url)}`),
};

// ---------------- Change Logs API ----------------
export const changesAPI = {
  getAll: () => api.get<ChangeLog[]>('/changes'),
};

// ---------------- Crawl API ----------------
export const crawlAPI = {
  crawlUrl: (url: string) => 
    api.post<CrawlResponse>('/crawl', null, { 
      params: { url } 
    }),
  
  crawlPage: (pageId: string, generateAiSummary: boolean = true) => 
    api.post<CrawlPageResponse>(`/crawl/${pageId}?generate_ai_summary=${generateAiSummary}`),
};

// ---------------- Health API ----------------
export const healthAPI = {
  check: () => api.get<HealthResponse>('/health'),
};

// ---------------- AI Summary API ----------------
export const aiAPI = {
  /**
   * Get AI summary for a specific version
   */
  getVersionSummary: (pageId: string, versionId: string): Promise<AISummaryResponse> => 
    api.get(`/pages/${pageId}/versions/${versionId}/summary`).then(res => res.data),

  /**
   * Regenerate AI summary for a version
   */
  regenerateSummary: (pageId: string, versionId: string): Promise<RegenerateSummaryResponse> => 
    api.post(`/pages/${pageId}/versions/${versionId}/regenerate-summary`).then(res => res.data),

  /**
   * Get AI service status
   */
  getAIStatus: (): Promise<AIStatusResponse> => 
    api.get('/ai/status').then(res => res.data),

  /**
   * Compare two versions with AI summary
   */
  compareVersions: (pageId: string, version1Id: string, version2Id: string): Promise<any> => 
    api.get(`/pages/${pageId}/versions/compare/${version1Id}/${version2Id}`).then(res => res.data),

  /**
   * Get versions with AI summaries
   */
  getVersionsWithSummaries: (pageId: string, limit: number = 10): Promise<{ success: boolean; data: PageVersion[] }> => 
    api.get(`/pages/${pageId}/versions?include_summary=true&limit=${limit}`).then(res => res.data),
};

// ---------------- MFA API Functions (for backward compatibility) ----------------
export const mfaAPI = {
  verify: authAPI.verifyMFA,
  sendCode: authAPI.sendMFACode,
  getStatus: authAPI.getMFAStatus,
  setup: authAPI.setupMFA,
  disable: authAPI.disableMFA,
  checkSession: authAPI.checkMFASession, // ✅ NEW
};

// ✅ ADDED: Export updatePage function for direct usage - FIXED return type
export const updatePage = async (pageId: string, data: PageUpdateData): Promise<UpdatedPageResponse> => {
  const response = await pagesAPI.update(pageId, data);
  return response.data; // Extract the data from AxiosResponse
};

// ---------------- Utility Functions ----------------
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleString();
};

export const formatTimeAgo = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  return `${Math.floor(diffInSeconds / 86400)} days ago`;
};

export const getStatusColor = (page: TrackedPage): string => {
  if (!page.is_active) return 'gray';
  if (page.last_change_detected) return 'green';
  if (page.last_checked) return 'blue';
  return 'yellow';
};

export const getStatusText = (page: TrackedPage): string => {
  if (!page.is_active) return 'Inactive';
  if (page.last_change_detected) return 'Changed';
  if (page.last_checked) return 'Monitored';
  return 'Pending';
};

// ✅ Token management utilities - UPDATED with MFA session support
export const tokenUtils = {
  getToken: (): string | null => {
    const token = localStorage.getItem('token');
    console.log(`[Token] Get token: ${token ? `Present (${token.length} chars)` : 'Missing'}`);
    return token;
  },
  
  setToken: (token: string): void => {
    console.log(`[Token] Setting token: ${token.substring(0, 20)}...`);
    localStorage.setItem('token', token);
    
    // Update axios defaults
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log(`[Token] Axios defaults updated`);
    
    const authHeader = getAuthorizationHeader();
    console.log(`[Token] New axios Authorization:`, authHeader ? `${authHeader.substring(0, 50)}...` : 'NOT SET');
  },
  
  removeToken: (): void => {
    console.log(`[Token] Removing token`);
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    console.log(`[Token] Axios Authorization cleared`);
  },
  
  isTokenValid: (): boolean => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log(`[Token] No token found`);
      return false;
    }
    
    try {
      // Basic JWT structure check (doesn't verify signature)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log(`[Token] Invalid token structure`);
        return false;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const isValid = payload.exp * 1000 > Date.now();
      console.log(`[Token] Token valid: ${isValid}, expires: ${new Date(payload.exp * 1000).toISOString()}`);
      return isValid;
    } catch (error) {
      console.error(`[Token] Token validation error:`, error);
      return false;
    }
  },
  
  // ✅ ADDED: Validate token with backend
  validateWithBackend: async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log(`[Token] No token to validate with backend`);
      return false;
    }
    
    try {
      console.log(`[Token] Validating token with backend...`);
      const response = await authAPI.validateToken(token);
      console.log(`[Token] Backend validation result:`, response.data);
      return response.data.valid === true;
    } catch (error: any) {
      console.error(`[Token] Backend validation failed:`, error.message);
      return false;
    }
  },
  
  // ✅ MFA Token Handling with "Remember Me" support
  setMFAToken: (tokenData: { access_token: string; token_type: string; mfa_session_token?: string; expires_in?: number }): void => {
    console.log(`[Token] Setting MFA token`);
    localStorage.setItem('token', tokenData.access_token);
    localStorage.setItem('token_type', tokenData.token_type);
    api.defaults.headers.common['Authorization'] = `Bearer ${tokenData.access_token}`;
    
    // ✅ Store MFA session token if provided (for "Remember Me")
    if (tokenData.mfa_session_token) {
      localStorage.setItem('mfa_session_token', tokenData.mfa_session_token);
      localStorage.setItem('mfa_verified_at', new Date().toISOString());
      console.log(`[Token] MFA session token stored (valid for 24 hours)`);
    }
    
    console.log(`[Token] MFA token set and axios configured`);
  },
  
  // ✅ NEW: Get MFA session token
  getMFASessionToken: (): string | null => {
    return localStorage.getItem('mfa_session_token');
  },
  
  // ✅ NEW: Check if MFA session is valid (based on timestamp)
  isMFASessionValid: (): boolean => {
    const mfaVerifiedAt = localStorage.getItem('mfa_verified_at');
    if (!mfaVerifiedAt) return false;
    
    try {
      const verifiedTime = new Date(mfaVerifiedAt);
      const now = new Date();
      const hoursElapsed = (now.getTime() - verifiedTime.getTime()) / (1000 * 60 * 60);
      const isValid = hoursElapsed < 24;
      console.log(`[Token] MFA session valid: ${isValid}, hours elapsed: ${hoursElapsed.toFixed(1)}`);
      return isValid;
    } catch {
      return false;
    }
  },
  
  // ✅ NEW: Clear MFA session
  clearMFASession: (): void => {
    console.log(`[Token] Clearing MFA session`);
    localStorage.removeItem('mfa_session_token');
    localStorage.removeItem('mfa_verified_at');
  },
  
  clearAuthData: (): void => {
    console.log(`[Token] Clearing all auth data`);
    localStorage.removeItem('token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user');
    localStorage.removeItem('mfa_session_token');
    localStorage.removeItem('mfa_verified_at');
    delete api.defaults.headers.common['Authorization'];
  },
  
  // ✅ NEW: Debug function to check token status
  debugToken: (): void => {
    console.log(`🔍 [Token Debug] === START ===`);
    const token = localStorage.getItem('token');
    console.log(`  - Token in localStorage:`, token ? `Present (${token.length} chars)` : 'Missing');
    console.log(`  - Token preview:`, token ? `${token.substring(0, 50)}...` : 'Missing');
    
    const authHeader = getAuthorizationHeader();
    console.log(`  - Axios Authorization header:`, authHeader ? `${authHeader.substring(0, 50)}...` : 'NOT SET');
    
    const mfaSessionToken = localStorage.getItem('mfa_session_token');
    console.log(`  - MFA Session Token:`, mfaSessionToken ? 'Present' : 'Missing');
    console.log(`  - MFA Session Valid:`, tokenUtils.isMFASessionValid());
    
    if (token) {
      try {
        const parts = token.split('.');
        console.log(`  - Token parts:`, parts.length);
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          console.log(`  - Token payload:`, payload);
          if (payload.exp) {
            const remaining = (payload.exp * 1000) - Date.now();
            console.log(`  - Time remaining:`, Math.floor(remaining / 1000), 'seconds');
          }
        }
      } catch (e) {
        console.error(`  - Token parse error:`, e);
      }
    }
    console.log(`🔍 [Token Debug] === END ===`);
  },
};

// ✅ Helper function to check if error is MFA-related
export const isMFARequiredError = (error: any): boolean => {
  const isMFA = (
    error?.message === 'MFA_REQUIRED' ||
    error?.name === 'MFARequiredError' ||
    error?.response?.data?.requires_mfa ||
    error?.requires_mfa
  );
  console.log(`[MFA] isMFARequiredError check: ${isMFA}`);
  return isMFA;
};

// ✅ Helper function to extract MFA data from error
export const getMFADataFromError = (error: any): MFALoginResponse | null => {
  if (isMFARequiredError(error)) {
    console.log(`[MFA] Extracting MFA data from error`);
    return {
      requires_mfa: true,
      email: error.response?.data?.email || error.email || '',
      message: error.response?.data?.message || error.message || 'MFA verification required',
      access_token: error.response?.data?.access_token,
      token_type: error.response?.data?.token_type,
    };
  }
  return null;
};

// ✅ NEW: Function to check MFA session before login attempt
export const checkMFASessionBeforeLogin = async (email: string): Promise<boolean> => {
  const mfaSessionToken = tokenUtils.getMFASessionToken();
  if (!mfaSessionToken || !tokenUtils.isMFASessionValid()) {
    console.log('[MFA] No valid MFA session found');
    return false;
  }
  
  try {
    console.log('[MFA] Checking MFA session with backend...');
    const response = await authAPI.checkMFASession(email, mfaSessionToken);
    console.log('[MFA] Session check result:', response.data);
    
    if (!response.data.mfa_required) {
      // Session is valid, we can attempt auto-login
      console.log('[MFA] Valid MFA session found, auto-login possible');
      return true;
    }
  } catch (error) {
    console.error('[MFA] Session check failed:', error);
  }
  
  return false;
};

// ✅ UPDATED: Test function to verify API connectivity - INCREASED TIMEOUT
export const testAPIConnection = async (): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`🧪 [API Test] Testing connection to ${API_BASE_URL}`);
    const response = await api.get('/health', {
      timeout: 120000, // ✅ CHANGED: 120000ms (2 minutes) to handle cold starts
      validateStatus: (status) => status < 500
    });
    console.log(`✅ [API Test] Connection successful:`, response.status);
    return { success: true, message: `API is reachable (${response.status})` };
  } catch (error: any) {
    console.error(`❌ [API Test] Connection failed:`, error.message);
    return { 
      success: false, 
      message: `API connection failed: ${error.message}` 
    };
  }
};

// ✅ ADDED: Convenience function for token validation
export const validateToken = async (): Promise<boolean> => {
  return tokenUtils.validateWithBackend();
};

// Export custom error type
export { MFARequiredError };

export default api;